import { UpdatedTranslations } from "entities/translations/types";
import { omit, isEmpty, isEqual } from "lodash";
import { useState, useCallback } from "react";
import {
  TranslationsParams,
  TranslationTypes,
  TranslationInitialValueParams,
} from "../types";

const useTranslations = <T extends Record<string, any>>(
  initialTableData: T[],
  translationType: TranslationTypes,
) => {
  const [updatedTranslations, setUpdatedTranslations] =
    useState<UpdatedTranslations | null>(null);
  const handleUpdatedTranslations = useCallback(
    ({ translationId, locale, value }: TranslationsParams) => {
      setUpdatedTranslations((prevTranslations) => {
        const trimmedValue = Array.isArray(value)
          ? value.join(", ").toString().trim()
          : value.toString().trim();
        const getInitialValue = ({
          translationId,
          locale,
        }: TranslationInitialValueParams) => {
          const translationInitialValue = initialTableData.find(
            (item) => item.id === translationId,
          );
          if (translationType === TranslationTypes.Dynamic) {
            return translationInitialValue?.translations?.[locale] ?? "";
          }
          if (translationType === TranslationTypes.Static) {
            return translationInitialValue?.[locale] ?? "";
          }
        };
        const initialValue = getInitialValue({ translationId, locale });
        if (isEqual(initialValue, trimmedValue)) {
          const updatedTranslation = omit(
            prevTranslations?.[translationId],
            locale,
          );

          return isEmpty(updatedTranslation)
            ? omit(prevTranslations, translationId)
            : { ...prevTranslations, [translationId]: updatedTranslation };
        }

        return {
          ...prevTranslations,
          [translationId]: {
            ...prevTranslations?.[translationId],
            [locale]: trimmedValue,
          },
        };
      });
    },
    [initialTableData, translationType],
  );

  return {
    updatedTranslations,
    handleUpdatedTranslations,
    setUpdatedTranslations,
  };
};

export default useTranslations;
